import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';
  // På undersider skal headeren altid se ud som ved scroll (også i toppen).
  const isScrolledState = !isHomePage || isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 25);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileAccordion(null);
  }, [location]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const toggleMobileAccordion = (name) => {
    setMobileAccordion((prev) => (prev === name ? null : name));
  };

  return (
    <header className={`site-header__wrapper fixed top-0 left-0 right-0 z-40 w-full transition-all duration-300 ${
      isScrolledState 
        ? 'bg-white shadow-md border-b border-neutral-200 h-[68px] sm:h-[78px] xl:h-[122px]' 
        : 'bg-transparent h-[68px] sm:h-[78px] xl:h-[122px]'
    }`}>
      <div className="w-full h-full relative flex items-center justify-between px-4 sm:px-6 xl:px-0">
        
        {/* 1. Logo (Dansk Tagbyg vektorlogo - Hvidt ikon før scroll, rødt ikon efter scroll) */}
        <div className="shrink-0 z-20 xl:absolute xl:left-[38px] xl:top-[26px]">
          <Link to="/" className="site-logo block group" title="Dansk Tagbyg">
            <svg
              viewBox="0 0 389.313 68.674"
              className="h-[32px] xs:h-[36px] sm:h-[44px] md:h-[50px] xl:h-[68px] w-auto max-w-[190px] xs:max-w-[220px] sm:max-w-none transition-transform group-hover:scale-[1.01]"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g fill={isScrolledState ? '#e70000' : '#ffffff'} className="transition-colors duration-300">
                <path
                  d="m81.5 238.9c1.326-.753 2.554.643 3.759 1.09q11.692 6.2 23.4 12.4a1.311 1.311 0 0 1 .9 1.526c.06 12.747.016 25.5.021 38.244.289 1.207-1.175 1.551-1.98 2.09q-11.83 6.257-23.648 12.542a2.643 2.643 0 0 1 -2.809.255q-12.391-6.567-24.791-13.137c-.45-.383-1.471-.516-1.3-1.269q-.034-19.609 0-39.22c-.172-.76.854-.881 1.3-1.257 8.408-4.39 16.723-8.926 25.148-13.264zm.542 2.005c-7.189 3.658-14.238 7.558-21.388 11.276a33.312 33.312 0 0 0 -3.791 2.139 13.92 13.92 0 0 0 -.114 2.68q.024 15.527 0 31.058a27.486 27.486 0 0 0 .112 3.738c6.556 3.706 13.325 7.081 19.961 10.677a58.016 58.016 0 0 0 5.53 2.79 16.556 16.556 0 0 0 2.382-1.161c7.641-4.185 15.509-8.02 23.043-12.373.112-12.437.1-24.881.028-37.31a27.286 27.286 0 0 0 -4.123-2.368c-6.641-3.545-13.309-7.038-19.945-10.6-.542-.168-1.095-.82-1.692-.551z"
                  transform="translate(-55.039 -238.68)"
                />
                <path
                  d="m79.334 240.808a2.03 2.03 0 0 1 2.384-.149c7.754 4.064 15.473 8.194 23.2 12.308.161 11.666.048 23.348.053 35.018.209.789-.574 1.136-1.15 1.452q-9.821 5.24-19.654 10.43a31.556 31.556 0 0 1 -3.451 1.716 7.653 7.653 0 0 1 -1.735-.75c-7.318-3.919-14.7-7.744-21.994-11.712-.945-.351-.581-1.45-.672-2.18.028-11.033.007-22.074.016-33.109-.236-.865.737-1.145 1.315-1.512 7.237-3.827 14.454-7.689 21.688-11.512zm.007 8.726a4.992 4.992 0 0 0 -2.464 1.652c.89-.078 1.771-.25 2.669-.291.778 2.618.225 5.376-.076 8.024-.08 1.579-.151 3.157-.257 4.736a3.838 3.838 0 0 0 2.129-.014c-.069-1.4-.177-2.8-.133-4.2.016-2.827-.665-5.764.31-8.5.677.016 1.345.138 2.019.2a10.5 10.5 0 0 0 .2-1.643 28.163 28.163 0 0 0 -4.397.037zm-15.941 5.196c.381 1.283 1.579 2.077 2.444 3.056 3.359 3.254 6.625 6.6 9.991 9.851a42.456 42.456 0 0 1 3.715 4.038c-1.257 1.636-2.965 2.887-4.344 4.422-1.944 2.194-4.236 4.082-6.216 6.246-1.687 1.829-3.747 3.336-5.257 5.314-.041.218-.124.661-.165.879 3.2-2.565 5.792-5.743 8.788-8.524 2.721-2.444 4.966-5.342 7.852-7.623 1.033.406 1.618 1.365 2.391 2.077 4.454 4.268 8.706 8.719 13.125 13.026.473.383.922 1.056 1.689.7-2.923-3.07-6-6.023-9.018-9.013-2.324-2.632-5.179-4.807-7.375-7.545 1.271-1.882 3.2-3.224 4.725-4.9 2.274-2.577 4.97-4.782 7.29-7.322 1.411-1.535 3.2-2.772 4.328-4.527-.207.046-.62.147-.824.193-2.673 2.219-4.91 4.858-7.435 7.221-3.022 2.781-5.6 6-8.857 8.554-1.891-1.333-3.233-3.215-4.908-4.761-2.731-2.506-5.207-5.245-7.884-7.79-1.343-1.202-2.297-2.879-4.055-3.572zm.741 8.866c-1.085 1.333-.4 3.059-.938 4.541-.477 2.194-1.595 4.289-1.4 6.579.011 1.127-.076 2.632 1.29 3.107.31-1.59.021-3.2.2-4.8a14.911 14.911 0 0 1 1.062-3.424l.544.142a15 15 0 0 1 1.067 7.522c-.385.89 1.143.372 1.122-.154 1.17-3.573-.863-7.026-1.471-10.48-.078-1.023.167-3.187-1.48-3.029zm30.723.493a12.311 12.311 0 0 0 -.381 3.977c.124 3.2-.321 6.391-.177 9.582l1.595.415c1.225-2.421 1.792-5.083 2.974-7.522a30.845 30.845 0 0 0 1.76-4.527c-1.948-.555-3.669-2.046-5.776-1.925zm-14.52 16.987c-.257.076-.773.234-1.026.31a52.468 52.468 0 0 0 .227 6.494c1.262 2.414.032 5.163.537 7.7.273-.021.826-.067 1.106-.087.2-2.52-.73-5.172.326-7.586a44.156 44.156 0 0 0 .076-6.471 2.054 2.054 0 0 0 -1.246-.36z"
                  transform="translate(-53.413 -236.551)"
                />
                <path
                  d="m73.8 251.47a16.123 16.123 0 0 1 2.776 1.15c1.058.317.344 1.8-.321 2.067-1.666-.3-3.121-1.625-2.889-3.325z"
                  transform="translate(-31.343 -222.261)"
                />
              </g>
              <g fill={isScrolledState ? '#000000' : '#ffffff'} className="transition-colors duration-300">
                <path
                  d="m100.938 247.469a11.944 11.944 0 0 1 8.681 3.318 11.362 11.362 0 0 1 3.412 8.515v4.69a11.328 11.328 0 0 1 -3.412 8.515 11.985 11.985 0 0 1 -8.681 3.3h-12.921v-3.407l3.059-.583v-20.339l-3.059-.583v-3.426zm-4.1 4.38v19.6h3.807a6.029 6.029 0 0 0 4.853-2.065 8.028 8.028 0 0 0 1.755-5.39v-4.729a7.971 7.971 0 0 0 -1.755-5.351 6.027 6.027 0 0 0 -4.853-2.063z"
                  transform="translate(-12.344 -227.301)"
                />
                <path
                  d="m99.855 272.4 2.051-.291 8.74-24.64h5.9l8.681 24.64 2.051.291v3.405h-9.784v-3.405l1.992-.351-1.262-3.892h-9.314l-1.262 3.892 1.992.351v3.405h-9.785zm10.415-8.426h6.59l-3.235-9.986h-.119z"
                  transform="translate(2.981 -227.301)"
                />
                <path
                  d="m142.338 247.469v3.426l-3.079.583v24.328h-5.739l-11.6-19.073-.117.021v15.064l3.077.583v3.405h-11.9v-3.406l3.056-.583v-20.339l-3.056-.583v-3.426h8.82l11.6 19.073.119-.018v-15.046l-3.079-.583v-3.426h11.9z"
                  transform="translate(19.977 -227.301)"
                />
                <path
                  d="m148.3 256.735h-4.321l-.75-3.894a7.776 7.776 0 0 0 -2.189-.817 12.893 12.893 0 0 0 -3.059-.349 5.951 5.951 0 0 0 -3.66.982 3.057 3.057 0 0 0 -1.292 2.54 2.766 2.766 0 0 0 1.352 2.363 18.8 18.8 0 0 0 4.667 1.957 18.314 18.314 0 0 1 7.21 3.444 7.1 7.1 0 0 1 2.455 5.624 6.7 6.7 0 0 1 -2.919 5.741 12.881 12.881 0 0 1 -7.694 2.1 16.891 16.891 0 0 1 -6.166-1.069 12.74 12.74 0 0 1 -4.587-2.958v-5.918h4.458l.613 4.029a6.989 6.989 0 0 0 2.357 1.156 11.3 11.3 0 0 0 3.325.459 5.809 5.809 0 0 0 3.6-.955 3.059 3.059 0 0 0 1.232-2.549 3.275 3.275 0 0 0 -1.172-2.621 11.971 11.971 0 0 0 -4.114-1.868 23.54 23.54 0 0 1 -7.733-3.465 6.525 6.525 0 0 1 -2.625-5.489 6.719 6.719 0 0 1 2.96-5.633 12.453 12.453 0 0 1 7.437-2.249 17.538 17.538 0 0 1 6.037.994 14 14 0 0 1 4.578 2.646z"
                  transform="translate(38.497 -227.526)"
                />
                <path
                  d="m137.966 272.4 3.056-.583v-20.339l-3.056-.583v-3.426h11.734v3.426l-2.919.583v7.572h2.506l5.523-7.921-1.7-.234v-3.426h11.39v3.426l-3.116.525-7.063 9.3 7.93 11.191 2.88.487v3.405h-11.383v-3.403l1.776-.234-5.524-8.192h-3.215v7.843l2.919.583v3.405h-11.738z"
                  transform="translate(52.321 -227.301)"
                />
                <path
                  d="m181.041 247.469v7.552h-4.417l-.239-3.171h-5.285v19.968l3.077.583v3.405h-11.915v-3.406l3.077-.583v-19.968h-5.269l-.236 3.171h-4.4v-7.552z"
                  transform="translate(74.936 -227.301)"
                />
                <path
                  d="m166.087 272.4 2.051-.291 8.74-24.64h5.9l8.681 24.64 2.051.291v3.405h-9.784v-3.405l1.992-.351-1.262-3.892h-9.312l-1.262 3.892 1.992.351v3.405h-9.784zm10.415-8.426h6.59l-3.235-9.986h-.119z"
                  transform="translate(88.727 -227.301)"
                />
                <path
                  d="m202.611 272.883a12.765 12.765 0 0 1 -4.084 2.462 18 18 0 0 1 -6.788 1.1 12.463 12.463 0 0 1 -9.468-3.844 13.917 13.917 0 0 1 -3.651-9.993v-.973a14.862 14.862 0 0 1 3.6-10.353 12.07 12.07 0 0 1 9.4-3.99 17.252 17.252 0 0 1 6.166 1.06 13.321 13.321 0 0 1 4.644 2.871v5.7h-4.261l-.808-3.8a5.927 5.927 0 0 0 -2.081-1.062 10.074 10.074 0 0 0 -2.969-.4 6.941 6.941 0 0 0 -5.858 2.744 11.533 11.533 0 0 0 -2.093 7.182v1.012a10.772 10.772 0 0 0 2.051 6.9 7.119 7.119 0 0 0 5.918 2.579 11 11 0 0 0 2.811-.294 6.185 6.185 0 0 0 1.689-.681v-4.8l-4.105-.312v-3.991h9.885z"
                  transform="translate(104.953 -227.532)"
                />
                <path
                  d="m203.448 247.469q5.07 0 7.91 1.946a6.591 6.591 0 0 1 2.841 5.819 6.074 6.074 0 0 1 -1.056 3.513 6.346 6.346 0 0 1 -3.068 2.327 6.106 6.106 0 0 1 3.878 2.336 6.9 6.9 0 0 1 1.292 4.144 7.293 7.293 0 0 1 -2.724 6.161q-2.723 2.089-7.712 2.09h-14.126v-3.405l3.059-.583v-20.339l-3.059-.583v-3.426zm-3.948 11.872h4.142a5.831 5.831 0 0 0 3.552-.934 3.2 3.2 0 0 0 1.244-2.726 3.376 3.376 0 0 0 -1.253-2.9 6.207 6.207 0 0 0 -3.738-.934h-3.947zm0 3.931v8.176h5.307a5.388 5.388 0 0 0 3.49-.984 3.532 3.532 0 0 0 1.2-2.91 4.5 4.5 0 0 0 -1.026-3.183 4.24 4.24 0 0 0 -3.235-1.1z"
                  transform="translate(120.569 -227.301)"
                />
                <path
                  d="m212.9 250.895-2.35.33 4.459 9.3h.119l4.458-9.3-2.329-.33v-3.426h10.87v3.426l-2.444.39-7.813 14.557v5.975l3.077.583v3.405h-11.9v-3.405l3.077-.583v-6.267l-7.673-14.266-2.428-.39v-3.426h10.877z"
                  transform="translate(135.26 -227.301)"
                />
                <path
                  d="m238.236 272.883a12.777 12.777 0 0 1 -4.082 2.462 18.015 18.015 0 0 1 -6.788 1.1 12.469 12.469 0 0 1 -9.47-3.844 13.916 13.916 0 0 1 -3.648-9.993v-.973a14.862 14.862 0 0 1 3.6-10.353 12.07 12.07 0 0 1 9.4-3.99 17.231 17.231 0 0 1 6.163 1.06 13.353 13.353 0 0 1 4.647 2.871v5.7h-4.258l-.81-3.8a5.887 5.887 0 0 0 -2.081-1.062 10.052 10.052 0 0 0 -2.969-.4 6.947 6.947 0 0 0 -5.858 2.744 11.532 11.532 0 0 0 -2.09 7.182v1.012a10.772 10.772 0 0 0 2.051 6.9 7.119 7.119 0 0 0 5.918 2.579 11 11 0 0 0 2.811-.294 6.166 6.166 0 0 0 1.687-.681v-4.8l-4.1-.312v-3.991h9.883z"
                  transform="translate(151.077 -227.532)"
                />
              </g>
            </svg>
          </Link>
        </div>

        {/* 2. Navigation Area (Desktop) - Placeret med ren afstand til telefonblokken */}
        <div className="hidden xl:flex flex-col items-end justify-center absolute right-[236px] top-[30px] z-10">
          
          {/* Øverste micro-links: Profil, Viden, Job, Garantier - flyttet 4-5px mod venstre */}
          <div className="site-nav__top flex items-center gap-5 mb-1.5 -translate-x-[34px]">
            <Link
              to="/profil"
              className={`font-['Open_Sans'] font-semibold text-[15px] lg:text-[15.5px] uppercase tracking-[0.6px] transition-colors ${
                isScrolledState ? 'text-[#363636] hover:text-[#BE0F12]' : 'text-white/85 hover:text-white'
              }`}
            >
              Profil
            </Link>
            <Link
              to="/viden"
              className={`font-['Open_Sans'] font-semibold text-[15px] lg:text-[15.5px] uppercase tracking-[0.6px] transition-colors ${
                isScrolledState ? 'text-[#363636] hover:text-[#BE0F12]' : 'text-white/85 hover:text-white'
              }`}
            >
              Viden
            </Link>
            <Link
              to="/job"
              className={`font-['Open_Sans'] font-semibold text-[15px] lg:text-[15.5px] uppercase tracking-[0.6px] transition-colors ${
                isScrolledState ? 'text-[#363636] hover:text-[#BE0F12]' : 'text-white/85 hover:text-white'
              }`}
            >
              Job
            </Link>
            <Link
              to="/garantier"
              className={`font-['Open_Sans'] font-semibold text-[15px] lg:text-[15.5px] uppercase tracking-[0.6px] transition-colors ${
                isScrolledState ? 'text-[#363636] hover:text-[#BE0F12]' : 'text-white/85 hover:text-white'
              }`}
            >
              Garantier
            </Link>
          </div>

          {/* Hovedmenu: Nyt tag, Prisberegner, Energiløsninger, Projekter, Kontakt - aligned med øverste linje */}
          <nav className="site-nav flex items-center gap-6 -translate-x-[34px]">
            
            {/* Nyt tag (med hover-dropdown) */}
            <div
              className="relative group py-1"
              onMouseEnter={() => setOpenDropdown('nyttag')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                to="/nyt-tag"
                className={`font-['Saira'] font-semibold text-[20px] lg:text-[20.5px] transition-colors flex items-center gap-1 ${
                  isScrolledState ? 'text-[#1c1c1c] hover:text-[#BE0F12]' : 'text-white hover:text-[#BE0F12]'
                }`}
              >
                Nyt tag
              </Link>

              {/* Dropdown Menu */}
              {openDropdown === 'nyttag' && (
                <div className={`absolute top-full left-0 w-56 shadow-2xl py-2 z-50 border-t-2 border-[#BE0F12] ${
                  isScrolledState ? 'bg-white border-x border-b border-neutral-200' : 'bg-[#161616]'
                }`}>
                  <Link
                    to="/processen"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Processen
                  </Link>
                  <Link
                    to="/nyt-tag"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Tagtyper
                  </Link>
                  <Link
                    to="/asbest-autoriseret"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Asbest autoriseret
                  </Link>
                  <Link
                    to="/prisberegner"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Beregn pris på nyt tag
                  </Link>
                </div>
              )}
            </div>

            {/* Prisberegner */}
            <Link
              to="/prisberegner"
              className={`font-['Saira'] font-semibold text-[20px] lg:text-[20.5px] transition-colors py-1 ${
                isScrolledState ? 'text-[#1c1c1c] hover:text-[#BE0F12]' : 'text-white hover:text-[#BE0F12]'
              }`}
            >
              Prisberegner
            </Link>

            {/* Energiløsninger (med hover-dropdown) */}
            <div
              className="relative group py-1"
              onMouseEnter={() => setOpenDropdown('energi')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                to="/energiloesninger"
                className={`font-['Saira'] font-semibold text-[20px] lg:text-[20.5px] transition-colors flex items-center gap-1 ${
                  isScrolledState ? 'text-[#1c1c1c] hover:text-[#BE0F12]' : 'text-white hover:text-[#BE0F12]'
                }`}
              >
                Energiløsninger
              </Link>

              {/* Dropdown Menu */}
              {openDropdown === 'energi' && (
                <div className={`absolute top-full left-0 w-64 shadow-2xl py-2 z-50 border-t-2 border-[#BE0F12] ${
                  isScrolledState ? 'bg-white border-x border-b border-neutral-200' : 'bg-[#161616]'
                }`}>
                  <Link
                    to="/energiloesninger"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Løsninger
                  </Link>
                  <Link
                    to="/ydelser/vinduer-og-doere"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Nye vinduer og døre
                  </Link>
                  <Link
                    to="/p/prisberegner-energiloesninger"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Beregn din pris for isolering af loft
                  </Link>
                  <Link
                    to="/ydelser/solceller"
                    className={`block px-5 py-2.5 font-['Saira'] font-normal text-sm transition-colors ${
                      isScrolledState ? 'text-neutral-700 hover:text-[#BE0F12] hover:bg-neutral-50' : 'text-neutral-200 hover:text-white hover:bg-neutral-800'
                    }`}
                  >
                    Solceller
                  </Link>
                </div>
              )}
            </div>

            {/* Projekter */}
            <Link
              to="/projekter"
              className={`font-['Saira'] font-semibold text-[20.5px] lg:text-[21px] transition-colors py-1 ${
                isScrolledState ? 'text-[#1c1c1c] hover:text-[#BE0F12]' : 'text-white hover:text-[#BE0F12]'
              }`}
            >
              Projekter
            </Link>

            {/* Kontakt */}
            <Link
              to="/kontakt"
              className={`font-['Saira'] font-semibold text-[20.5px] lg:text-[21px] transition-colors py-1 ${
                isScrolledState ? 'text-[#1c1c1c] hover:text-[#BE0F12]' : 'text-white hover:text-[#BE0F12]'
              }`}
            >
              Kontakt
            </Link>

          </nav>
        </div>

        {/* 3. Telefonblok: Reduceret bredde ~5-7% (til 212px), øget højde ~5-8% (til 122px), flush mod top og højre */}
        <a
          href="tel:28687450"
          className="hidden xl:flex absolute right-0 top-0 w-[212px] h-[122px] bg-[#BE0F12] hover:bg-[#A30D0F] text-white flex-col justify-center items-center text-center leading-tight transition-colors shrink-0 rounded-none shadow-lg z-20 px-5"
        >
          <span className="block font-['Open_Sans'] font-semibold text-[11.5px] uppercase tracking-[0.8px] text-white/95 mb-1">
            Ring til os:
          </span>
          <div className="site-nav__phone flex items-center justify-center gap-1.5 font-['Saira'] font-bold text-[19px] text-white tracking-wider">
            <span>28</span>
            <span>68</span>
            <span>74</span>
            <span>50</span>
          </div>
        </a>

        {/* 4. Mobil & tablet handlinger (< xl) */}
        <div className="flex xl:hidden items-center gap-2 sm:gap-3 shrink-0 z-20">
          <a
            href="tel:28687450"
            className="bg-[#BE0F12] hover:bg-[#A30D0F] text-white px-2.5 xs:px-3 sm:px-4 py-2 sm:py-2.5 text-center leading-tight transition-all duration-200 text-xs sm:text-sm font-['Saira'] font-bold flex items-center gap-1.5 sm:gap-2 shadow-sm rounded-none active:scale-95 shrink-0"
            aria-label="Ring til 28 68 74 50"
          >
            <i className="fa-solid fa-phone text-xs sm:text-sm" aria-hidden="true"></i>
            <span className="hidden xs:inline">28 68 74 50</span>
          </a>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`w-10 h-10 sm:w-11 sm:h-11 flex flex-col items-center justify-center gap-1.5 border cursor-pointer focus:outline-none transition-all duration-200 shrink-0 ${
              isScrolledState 
                ? 'bg-neutral-100 hover:bg-neutral-200 border-neutral-300 text-neutral-900' 
                : 'bg-black/50 hover:bg-black/70 border-white/20 text-white'
            }`}
            aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
          >
            <span className={`block w-5 sm:w-6 h-0.5 transition-transform duration-300 ${isScrolledState ? 'bg-neutral-900' : 'bg-white'} ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`block w-5 sm:w-6 h-0.5 transition-opacity duration-300 ${isScrolledState ? 'bg-neutral-900' : 'bg-white'} ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-5 sm:w-6 h-0.5 transition-transform duration-300 ${isScrolledState ? 'bg-neutral-900' : 'bg-white'} ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

      </div>

      {/* 5. Mobil Drawer / Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 xl:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-xs"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Drawer content */}
          <div className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-[#181818] text-white p-6 overflow-y-auto shadow-2xl flex flex-col justify-between">
            <div>
              {/* Header inside drawer */}
              <div className="flex items-center justify-between pb-6 border-b border-white/10 mb-6">
                <span className="font-['Saira'] font-bold text-lg text-white">Menu</span>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer"
                  aria-label="Luk menu"
                >
                  <i className="fa-solid fa-xmark text-xl"></i>
                </button>
              </div>

              {/* Hovedlinks i drawer */}
              <nav className="space-y-3 font-['Saira'] font-semibold text-lg">
                
                {/* Nyt tag m. accordion */}
                <div>
                  <button
                    type="button"
                    onClick={() => toggleMobileAccordion('nyttag')}
                    className="w-full flex items-center justify-between py-2 text-white hover:text-[#e70000] cursor-pointer"
                  >
                    <span>Nyt tag</span>
                    <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileAccordion === 'nyttag' ? 'rotate-180 text-[#e70000]' : ''}`}></i>
                  </button>
                  {mobileAccordion === 'nyttag' && (
                    <div className="pl-4 pb-2 space-y-2 text-sm font-normal text-neutral-300 font-['Open_Sans'] border-l border-white/10 ml-2 mt-1">
                      <Link to="/processen" className="block py-1 hover:text-white">Processen</Link>
                      <Link to="/nyt-tag" className="block py-1 hover:text-white">Tagtyper</Link>
                      <Link to="/asbest-autoriseret" className="block py-1 hover:text-white">Asbest autoriseret</Link>
                      <Link to="/prisberegner" className="block py-1 hover:text-white">Beregn pris på nyt tag</Link>
                    </div>
                  )}
                </div>

                {/* Prisberegner */}
                <div>
                  <Link to="/prisberegner" className="block py-2 text-white hover:text-[#e70000]">
                    Prisberegner
                  </Link>
                </div>

                {/* Energiløsninger m. accordion */}
                <div>
                  <button
                    type="button"
                    onClick={() => toggleMobileAccordion('energi')}
                    className="w-full flex items-center justify-between py-2 text-white hover:text-[#e70000] cursor-pointer"
                  >
                    <span>Energiløsninger</span>
                    <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileAccordion === 'energi' ? 'rotate-180 text-[#e70000]' : ''}`}></i>
                  </button>
                  {mobileAccordion === 'energi' && (
                    <div className="pl-4 pb-2 space-y-2 text-sm font-normal text-neutral-300 font-['Open_Sans'] border-l border-white/10 ml-2 mt-1">
                      <Link to="/energiloesninger" className="block py-1 hover:text-white">Løsninger</Link>
                      <Link to="/ydelser/vinduer-og-doere" className="block py-1 hover:text-white">Nye vinduer og døre</Link>
                      <Link to="/p/prisberegner-energiloesninger" className="block py-1 hover:text-white">Beregn din pris for isolering af loft</Link>
                      <Link to="/ydelser/solceller" className="block py-1 hover:text-white">Solceller</Link>
                    </div>
                  )}
                </div>

                {/* Projekter */}
                <div>
                  <Link to="/projekter" className="block py-2 text-white hover:text-[#e70000]">
                    Projekter
                  </Link>
                </div>

                {/* Kontakt */}
                <div>
                  <Link to="/kontakt" className="block py-2 text-white hover:text-[#e70000]">
                    Kontakt
                  </Link>
                </div>

              </nav>

              {/* Sekundære links (Profil, Viden, Job, Garantier) */}
              <div className="pt-8 mt-6 border-t border-white/10 space-y-2.5 font-['Open_Sans'] text-sm uppercase tracking-wider text-neutral-400">
                <Link to="/profil" className="block hover:text-white">Profil</Link>
                <Link to="/viden" className="block hover:text-white">Viden</Link>
                <Link to="/job" className="block hover:text-white">Job</Link>
                <Link to="/garantier" className="block hover:text-white">Garantier</Link>
              </div>
            </div>

            {/* Rød telefonknap nederst i mobilmenu */}
            <div className="pt-8 mt-6 border-t border-white/10">
              <a
                href="tel:28687450"
                className="block bg-[#e70000] hover:bg-[#c90000] text-white p-3.5 text-center font-['Saira'] font-bold text-lg"
              >
                Ring til os: 28 68 74 50
              </a>
            </div>

          </div>
        </div>
      )}

    </header>
  );
}
